import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DailySchedule, IProgramSetup, IProgramSetupForm } from 'shared/interfaces/IProgramSetup';
import * as Yup from 'yup';

interface Props {
  programSetup?: IProgramSetup;
}

const defaultTimeslot = format(new Date('1990-01-01 00:00:00'), '1990-01-01 HH:mm:ss');

export const useProgramSetupForm = ({ programSetup }: Props) => {
  const getInitialValue = (
    key: keyof IProgramSetup,
    defaultValue: any,
    cb?: (key: string | number | symbol, record: IProgramSetup) => any,
  ) => {
    if (programSetup) {
      if (cb) {
        const value = cb(key, programSetup);
        return value;
      }
      return programSetup[key];
    } else {
      return defaultValue;
    }
  };

  const getDailyScheduleData = (dailySchedule?: DailySchedule) => {
    if (!dailySchedule) return undefined;

    const schedule: DailySchedule = dailySchedule;

    const withDate = (time?: string | null) => (time && !time.includes('1990-01-01') ? `1990-01-01 ${time}` : time);

    for (const day in schedule) {
      if (schedule[day]) {
        schedule[day].timeslot_start = withDate(schedule[day].timeslot_start);
        schedule[day].timeslot_end = withDate(schedule[day].timeslot_end);
        schedule[day].automation_enabled = schedule[day].automation_enabled ?? false;
      }
    }

    return schedule;
  };

  const getChapterScheduleData = (chapterSchedule?: any) => {
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const result: any = {};
    allDays.forEach((day) => {
      result[day] = {
        chapter_start: (chapterSchedule && chapterSchedule[day] && chapterSchedule[day].chapter_start) ?? '',
        chapter_end: (chapterSchedule && chapterSchedule[day] && chapterSchedule[day].chapter_end) ?? '',
      };
    });
    return result;
  };

  // Existing records store these as null (not just absent) for anything never
  // filled in via the now-removed Title Location OCR cards — .default() only
  // coerces undefined, so every field here also needs .nullable() or editing an
  // old record throws "must be a `string` type ... final value was: `null`".
  const titleSetupObject = {
    ocr_lang: Yup.string().label('OCR Language').notRequired().nullable().default(''),
    top_left_up: Yup.string().label('Top Left').notRequired().nullable().default(''),
    top_left_down: Yup.string().label('Bottom Left').notRequired().nullable().default(''),
    top_right_up: Yup.string().label('Top Right').notRequired().nullable().default(''),
    top_right_down: Yup.string().label('Bottom Right').notRequired().nullable().default(''),
    on_off: Yup.mixed().oneOf([1, 0, null]).label('Status').notRequired().nullable().default(1),
    machine_ip_address: Yup.string()
      .matches(
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        {
          message: 'Invalid IP address',
          excludeEmptyString: true,
        },
      )
      .label('Machine IP Address')
      .notRequired()
      .nullable()
      .default(''),
    minimum_article_length_in_seconds: Yup.number().label('Minimum Article Length').notRequired().nullable().default(0),
  };

  const recordingTitleSetupObject = {
    folder_name: Yup.string().label('Folder Name').notRequired().nullable().default(''),
    file_extension: Yup.string().label('File Extension').notRequired().nullable().default(''),
    recording_machine_name: Yup.string().label('Recording Machine Name').notRequired().nullable().default(''),
    filename: Yup.string().label('Filename').notRequired().nullable().default(''),
    sagap_process: Yup.boolean().label('Sagap Process').notRequired().nullable().default(false),
  };

  // const requiredTitleSetupObject = {
  //   ocr_lang: Yup.string().label('OCR Language').required(),
  //   top_left_up: Yup.string()
  //     .label('Top Left')
  //     .matches(/^(\d+:)\d+$/, 'Top Left must be x:y coordinates')
  //     .required(),
  //   top_left_down: Yup.string()
  //     .label('Bottom Left')
  //     .matches(/^(\d+:)\d+$/, 'Bottom Left must be x:y coordinates')
  //     .required(),
  //   top_right_up: Yup.string()
  //     .label('Top Right')
  //     .matches(/^(\d+:)\d+$/, 'Top Right must be x:y coordinates')
  //     .required(),
  //   top_right_down: Yup.string()
  //     .label('Bottom Right')
  //     .matches(/^(\d+:)\d+$/, 'Bottom Right must be x:y coordinates')
  //     .required(),
  //   on_off: Yup.mixed().oneOf([1, 0]).label('Status').required(),
  //   machine_ip_address: Yup.string()
  //     .matches(
  //       /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  //       {
  //         message: 'Invalid IP address',
  //         excludeEmptyString: true,
  //       },
  //     )
  //     .label('Machine IP Address')
  //     .required(),
  //   minimum_article_length_in_seconds: Yup.number().label('Minimum Article Length').required(),
  // };

  // const requiredRecordingTitleSetupObject = {
  //   folder_name: Yup.string().label('Folder Name').required(),
  //   file_extension: Yup.string().label('File Extension').required(),
  //   recording_machine_name: Yup.string().label('Recording Machine Name').required(),
  //   filename: Yup.string().label('Filename').required(),
  // };

  const ProgramSetupSchema: Yup.SchemaOf<IProgramSetupForm> = Yup.object({
    anchors: Yup.array().min(1).required().label('Anchors'),
    channel_obj: Yup.string().required().label('TV Channel'),
    program_obj: Yup.string().required().label('TV Program'),
    station_obj: Yup.string().notRequired().nullable().label('Radio Station'),
    station_program_obj: Yup.mixed()
      .when('station_obj', {
        is: (channel) => isEmpty(channel),
        then: Yup.string().notRequired().nullable(),
        otherwise: Yup.string().required().typeError('Radio Station Program is Required'),
      })
      .label('Radio Station Program'),

    country: Yup.string().required().label('Country'),
    segment_type: Yup.string().required().nullable().label('Segment Type'),

    youtube_scrape_details: Yup.object({
      youtube_url: Yup.string().nullable(),
      youtube_channel_filter: Yup.string().nullable(),
      youtube_channel_name: Yup.string().nullable(),
      youtube_last_upload_url: Yup.string().nullable(),
    }),
    facebook_scraper_details: Yup.object({
      facebook_url: Yup.string().nullable(),
      facebook_last_upload_url: Yup.string().nullable(),
    }),
    local_recording_details: Yup.object({
      local_recording_path: Yup.string().nullable().label('Local Recording Path'),
      source_folder: Yup.string().notRequired().nullable().label('Source Folder'),
    }),
    // Existing records store null (not just absent) for slots nobody's touched —
    // each field needs its own .nullable(), it doesn't inherit from the parent.
    daily_schedule: Yup.object({
      monday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      tuesday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      wednesday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      thursday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      friday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      saturday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      sunday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
    }),
    chapter_schedule: Yup.object({
      monday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      tuesday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      wednesday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      thursday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      friday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      saturday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      sunday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
    }).notRequired(),

    youtube_title_setup: Yup.object({
      ...titleSetupObject,
    })
      .notRequired()
      .nullable(),
    facebook_title_setup: Yup.object({
      ...titleSetupObject,
    })
      .notRequired()
      .nullable(),
    recording_title_setup: Yup.object({
      ...titleSetupObject,
      ...recordingTitleSetupObject,
    })
      .notRequired()
      .nullable(),
  });

  const defaultValues: IProgramSetupForm = {
    anchors: getInitialValue('anchors', [], (_, record: IProgramSetup) => record.anchors.map((anchor) => anchor._id)),
    channel_obj: getInitialValue(
      'channel_details',
      undefined,
      (_, record: IProgramSetup) => record.channel_details._id,
    ),
    program_obj: getInitialValue(
      'program_details',
      undefined,
      (_, record: IProgramSetup) => record.program_details._id,
    ),
    station_obj: getInitialValue(
      'station_details',
      null,
      (_, record: IProgramSetup) => record.station_details?._id || null,
    ),
    station_program_obj: getInitialValue(
      'station_program_details',
      null,
      (_, record: IProgramSetup) => record.station_program_details?._id || null,
    ),
    youtube_scrape_details: getInitialValue('youtube_scrape_details', {
      youtube_url: '',
      youtube_channel_filter: 'video',
      youtube_channel_name: '',
      youtube_last_upload_url: '',
    }),
    facebook_scraper_details: getInitialValue('facebook_scraper_details', {
      facebook_url: '',
      facebook_last_upload_url: '',
    }),
    local_recording_details: getInitialValue('local_recording_details', {
      local_recording_path: '',
      source_folder: '',
    }),
    country: getInitialValue('country', 'PHILIPPINES'),
    segment_type: getInitialValue('segment_type', null),
    youtube_title_setup: getInitialValue('youtube_title_setup', null),
    facebook_title_setup: getInitialValue('facebook_title_setup', null),
    recording_title_setup: getInitialValue('recording_title_setup', null),
    daily_schedule: getInitialValue(
      'daily_schedule',
      {
        monday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        tuesday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        wednesday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        thursday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        friday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        saturday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
        sunday: {
          timeslot_start: defaultTimeslot,
          timeslot_end: defaultTimeslot,
          automation_enabled: false,
        },
      },
      () => getDailyScheduleData(programSetup?.daily_schedule),
    ),
    chapter_schedule:
      programSetup && programSetup.chapter_schedule
        ? getChapterScheduleData(programSetup.chapter_schedule)
        : {
            monday: { chapter_start: '', chapter_end: '' },
            tuesday: { chapter_start: '', chapter_end: '' },
            wednesday: { chapter_start: '', chapter_end: '' },
            thursday: { chapter_start: '', chapter_end: '' },
            friday: { chapter_start: '', chapter_end: '' },
            saturday: { chapter_start: '', chapter_end: '' },
            sunday: { chapter_start: '', chapter_end: '' },
          },
  };

  const form = useForm<IProgramSetupForm>({
    resolver: yupResolver(ProgramSetupSchema),
  });

  // console.log(form.formState.errors);

  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programSetup]);

  return {
    form,
  };
};
